import EventService from './event-service';
import _ from 'lodash';
import TransferManager from './transfer-manager';
import {
	initSession,
	registerStatusCallback,
	deregisterStatusCallback,
	registerActivityCallback,
	startTransfer as sdkStartTransfer,
	getStatus as sdkGetStatus,
	getAllTransfers as sdkGetAllTransfers,
	showSelectFileDialog as sdkShowSelectFileDialog,
	showSelectFolderDialog as sdkShowSelectFolderDialog,
	resumeTransfer as sdkResumeTransfer,
	removeTransfer as sdkRemoveTransfer,
	showDirectory as sdkShowDirectory,
	stopTransfer as sdkStopTransfer,
	showTransferManager as sdkShowTransferManager,
	createDropzone as sdkCreateDropzone
} from '@ibm-aspera/sdk';

const CONNECT_UNAVAILABLE_STATUSES = new Set([null, 'FAILED', 'STOPPED']);

// Connect is still on its way somewhere: the SDK reports RETRYING while it
// relaunches Connect, so neither of these is an answer to wait on.
const TRANSIENT_STATUSES = new Set(['INITIALIZING', 'RETRYING']);

// Longest a caller waits on the plugin status before falling back. A missing
// Connect reports FAILED in ~5s and settles us then, so this cap only applies
// when no status arrives at all — which is mostly the browser holding its local
// network prompt open while the user decides. Give them room: cutting that
// short would drop a perfectly good Aspera transfer to http mid-decision.
const SESSION_SETTLE_TIMEOUT = 60000;

// How long a retry gives Connect to answer before calling it missing. The SDK
// re-polls every couple of seconds, so this is a few attempts' worth.
const CONNECT_GRACE_PERIOD = 5000;

// The same wait, for the moment just after the permission was granted. The SDK
// starts probing loopback the instant a session opens — which is while the
// browser's dialog is still up — so by the time someone clicks Allow it has
// already given up and gone to FAILED. It keeps polling in the background every
// couple of seconds, so Connect does turn up; the grace period above is simply
// too short to still be listening when it does.
export const CONNECT_DETECT_AFTER_GRANT = 15000;

let sessionPromise = null;

const TRANSFER_STATUS = {
	CANCELLED: 'cancelled',
	COMPLETED: 'completed',
	FAILED: 'failed',
	INITIATING: 'initiating',
	QUEUED: 'queued',
	REMOVED: 'removed',
	RUNNING: 'running',
	WILLRETRY: 'willretry'
};

const Aspera = {
	TRANSFER_STATUS,

	Connect: {
		startTransfer: (specs, options) => Aspera.ensureSession()
			.then(() => sdkStartTransfer(specs, options)),
		removeTransfer: (uuid) => sdkRemoveTransfer(uuid),
		getAllTransfers: () => Aspera.ensureSession()
			.then(() => sdkGetAllTransfers())
			.then((result) => ({
				transfers: Array.isArray(result) ? result : (result?.transfers ?? [])
			})),
		addEventListener: (event, cb) => {
			if(event === 'transfer') {
				EventService.on('Connect:Plugin:Transfer', (_eventType, data) => cb(event, data));
			}
		}
	},

	getStatus: () => {
		try {
			return sdkGetStatus();
		}
		catch{
			return null;
		}
	},

	/**
	 * Resolves true as soon as Connect reports RUNNING, false if it has not by
	 * the deadline.
	 *
	 * ensureSession caches its promise, so anyone awaiting it a second time gets
	 * whatever status happens to be current — but the SDK keeps polling after a
	 * FAILED and can come up later. Reading the status straight after a retry
	 * would call an installed Connect missing.
	 */
	waitForRunning: (timeout = CONNECT_GRACE_PERIOD) => new Promise((resolve) => {
		if( Aspera.getStatus() === 'RUNNING' ) {
			resolve(true);
			return;
		}

		let callbackId;
		let isSettled = false;

		const finish = (isRunning) => {
			if( isSettled ) {
				return;
			}

			isSettled = true;
			clearTimeout(timer);

			try {
				deregisterStatusCallback(callbackId);
			}
			catch{
				// nothing registered, or the SDK is unavailable
			}

			resolve(isRunning);
		};

		const timer = setTimeout(() => finish(false), timeout);

		try {
			callbackId = registerStatusCallback((status) => {
				if( status === 'RUNNING' ) {
					finish(true);
				}
			});

			// The SDK calls a new callback straight away when it already holds a
			// status, so this can have settled before the id came back — leaving
			// finish() with nothing to deregister and the callback in place for the
			// life of the app.
			if( isSettled ) {
				deregisterStatusCallback(callbackId);
			}
		}
		catch{
			finish(false);
		}
	}),

	startTransfer: (transferSpec, asperaSpec) => Aspera.ensureSession()
		.then(() => sdkStartTransfer(transferSpec, asperaSpec)),

	showSelectFileDialog: (options) => Aspera.ensureSession()
		.then(() => sdkShowSelectFileDialog(options)),

	showSelectFolderDialog: (options) => Aspera.ensureSession()
		.then(() => sdkShowSelectFolderDialog(options)),

	createDropzone: (callback, elementId) => Aspera.ensureSession()
		.then(() => sdkCreateDropzone(callback, elementId)),

	/**
	 * Starts the Aspera session the first time something actually needs it.
	 *
	 * The SDK reaches Connect over loopback (http://127.0.0.1), which Chromium
	 * gates behind the Local Network Access permission — starting a session
	 * during app boot prompted every visitor, on the login page, before they had
	 * even signed in. Resolves once the plugin status settles so callers can
	 * read getStatus() and choose between an Aspera transfer and the http
	 * fallback. Safe to call repeatedly; only the first call starts a session.
	 */
	ensureSession: () => {
		if( sessionPromise ) {
			return sessionPromise;
		}

		sessionPromise = new Promise((resolve) => {
			if( window.Cypress ) {
				resolve();
				return;
			}

			// don't leave a caller hanging if Connect never answers
			setTimeout(resolve, SESSION_SETTLE_TIMEOUT);

			// Nothing in here may throw: a rejected session promise would strand
			// every caller awaiting it, including the download that wants to fall
			// back to http.
			try {
				// Registered before the session starts so a status that is already
				// known settles us immediately. The SDK keeps every callback, so
				// this sits alongside the broadcasting one from init().
				registerStatusCallback((status) => {
					if( !TRANSIENT_STATUSES.has(status) ) {
						resolve();
					}
				});

				initSession({
					appId: 'pixwel',
					connectSettings: {
						fallback: true,
						minVersion: '3.6.0',
						dragDropEnabled: true
					}
				});
			}
			catch{
				EventService.broadcast('Connect:NotInstalled');
				resolve();
			}
		});

		return sessionPromise;
	},

	check: () => {
		const status = Aspera.getStatus();
		if(status !== 'RUNNING') {
			// Nothing has needed Aspera yet, so there is no session and no status
			// to report — start one rather than declaring Connect missing.
			if( !sessionPromise ) {
				Aspera.ensureSession();
				return false;
			}

			if(CONNECT_UNAVAILABLE_STATUSES.has(status)) {
				EventService.broadcast('Connect:NotInstalled');
			}
			return false;
		}
		return true;
	},

	isDisabled: false,

	init: () => {
		try {
			registerStatusCallback((status) => {
				EventService.broadcast('Connect:Plugin:Status', 'status', status);
			});

			registerActivityCallback((data) => {
				TransferManager.eventHandler(data);
				EventService.broadcast('Connect:Plugin:Transfer', 'transfer', data);
			});
		}
		catch{
			EventService.broadcast('Connect:NotInstalled');
		}

		const addEvent = (event, cb) => {
			return EventService.on('Connect:' + event, (...args) => {
				return Aspera.check() ? cb(...args) : null;
			});
		};

		const selectFilesFolders = (type, e, options) => {
			return EventService.emit('Connect:Select' + type, {
				success: (files) => {
					if(!files.dataTransfer.files.length) {
						return;
					}
					const specs = {};
					specs[options.destination.nodeId] = {
						info: options.node,
						rootId: options.destination.fileId,
						files: files.dataTransfer.files.map((file) => {
							return { source: file.name };
						}),
						success: options.success
					};
					return EventService.emit('Connect:Upload', specs);
				}
			}, {
				title: 'Select ' + type
			});
		};

		const transferWithSpecs = (direction, specs) => {
			if(!Aspera.check()) {
				return;
			}

			for(let nodeId in specs) {
				const spec = specs[nodeId];

				const Spec = {
					direction,
					remote_host: spec.info?.host,
					paths: (spec.files ?? []).map((f) => ({ source: f.source }))
				};

				if(direction === 'receive' && spec.name) {
					Spec.paths = Spec.paths.map((path) => ({ ...path, destination: spec.name }));
				}

				if(!(Spec.direction && Spec.paths.length && Spec.remote_host)) {
					return;
				}

				EventService.emit('Connect:TransferStarted',
					Aspera.startTransfer(Spec, {})
						.then((data) => {
							if(typeof spec.success === 'function') {
								return spec.success(data);
							}
						})
						.catch((error) => {
							if(typeof spec.error === 'function') {
								spec.error(error);
							}
							throw error;
						}));
			}
		};

		_.forEach({
			'SelectFilesAndUpload': (e, options) => {
				selectFilesFolders('Files', e, options);
			},
			'SelectFoldersAndUpload': (e, options) => {
				selectFilesFolders('Folders', e, options);
			},
			'SelectFiles': (e, cb, options) => {
				const onSuccess = typeof cb === 'function' ? cb : cb?.success;
				const onError = cb?.error ?? _.noop;
				sdkShowSelectFileDialog(options).then(onSuccess)
					.catch(onError);
			},
			'SelectFolders': (e, cb, options) => {
				const onSuccess = typeof cb === 'function' ? cb : cb?.success;
				const onError = cb?.error ?? _.noop;
				sdkShowSelectFolderDialog(options).then(onSuccess)
					.catch(onError);
			},
			'Download': (e, specs) => {
				transferWithSpecs('receive', specs);
			},
			'Upload': (e, specs) => {
				transferWithSpecs('send', specs);
			},
			'Plugin:ResumeTransfer': (e, uuid) => {
				sdkResumeTransfer(uuid);
			},
			'Plugin:RemoveTransfer': (e, uuid) => {
				sdkRemoveTransfer(uuid);
			},
			'Plugin:ShowDirectory': (e, uuid) => {
				sdkShowDirectory(uuid);
			},
			'Plugin:StopTransfer': (e, uuid) => {
				sdkStopTransfer(uuid);
			},
			'Plugin:ShowTransferWindow': () => {
				sdkShowTransferManager();
			}
		}, (fn, name) => {
			addEvent(name, fn);
		});
	}
};

export default Aspera;
